import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
 
import { DELETE, GET, POST } from '../service/base-api-service';
import { getIsAdminApi, getUser, getUserRole } from './auth.store';
 
const initialState = {
  list: [],
  isLoading: false,
  isSaving: false,
  loadingById: {},
  error: null,
};
 
const getBasePath = (isAdmin) => `v1/${isAdmin ? 'admin/' : ''}report-email-subscriptions`;
 
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const getFullApiUrl = (endpoint) => {
  const base = String(process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');
  const path = String(endpoint || '').replace(/^\/+/, '');
  return `${base}/${path}`;
};

const getAuthHeaders = () => {
  const accessToken = localStorage.getItem('accessToken');
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

const getFileNameFromDisposition = (contentDisposition, fallbackFileName) => {
  const disposition = contentDisposition || '';
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return fallbackFileName;
};
 
const normalizeThunkError = (err, fallbackMessage) => ({
  status: err?.status,
  message: err?.message || err?.data?.message || fallbackMessage,
  data: err?.data,
});
 
const normalizeSubscription = (item = {}) => {
  return {
    id: item.id,
    userId: item.userId || item.user?.id,
    fullName: item.fullName || item.userName || item.user?.fullName || item.user?.name || '-',
    email: item.email || item.userEmail || item.user?.email || '-',
    reportType: item.reportType,
    region: item.region,
    createdAt: item.createdAt || item.createdDate || item.lastModified || null,
  };
};
 
const toBlob = (data) => {
  if (data instanceof Blob) {
    return data;
  }
 
  if (data instanceof ArrayBuffer) {
    return new Blob([data], { type: XLSX_MIME_TYPE });
  }
 
  if (ArrayBuffer.isView(data)) {
    return new Blob([data.buffer], { type: XLSX_MIME_TYPE });
  }
 
  if (data?.fileBytes && Array.isArray(data.fileBytes)) {
    return new Blob([new Uint8Array(data.fileBytes)], { type: XLSX_MIME_TYPE });
  }
 
  if (typeof data === 'string') {
    return new Blob([data], { type: XLSX_MIME_TYPE });
  }
 
  return null;
};
 
const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
 
export const fetchEmailSubscriptions = createAsyncThunk(
  'manageEmails/fetchSubscriptions',
  async (_, { getState }) => {
    const isAdmin = getIsAdminApi(getState());
    const data = await GET(getBasePath(isAdmin));
    return (Array.isArray(data) ? data : []).map(normalizeSubscription);
  },
);
 
export const createEmailSubscription = createAsyncThunk(
  'manageEmails/createSubscription',
  async ({ userId, reportType, region }, { getState, rejectWithValue }) => {
    const state = getState();
    const isAdmin = getIsAdminApi(state);
    const currentUser = getUser(state);
    const currentUserRole = getUserRole(state);
    const endpoint = getBasePath(isAdmin);
    const payload = {
      userId: Number(userId),
      reportType,
      region,
    };
 
    console.log('[manage-emails] create subscription request', {
      endpoint,
      payload,
      currentUser,
      currentUserRole,
    });
 
    try {
      const data = await POST(endpoint, payload);
      return normalizeSubscription(data);
    } catch (err) {
      const rawDetails = err?.data?.details;
      const detailsText = Array.isArray(rawDetails)
        ? rawDetails
            .map((item) => {
              if (typeof item === 'string') return item;
              if (item?.message) return item.message;
              if (item?.detail) return item.detail;
              if (item?.description) return item.description;
              return JSON.stringify(item);
            })
            .join(' | ')
        : rawDetails;
 
      console.error('[manage-emails] create subscription failed', {
        endpoint,
        payload,
        currentUserRole,
        status: err?.status,
        message: err?.message,
        data: err?.data,
        detailsText,
      });
 
      return rejectWithValue(normalizeThunkError(err, 'Unable to create subscription'));
    }
  },
);
 
export const deleteEmailSubscription = createAsyncThunk(
  'manageEmails/deleteSubscription',
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const isAdmin = getIsAdminApi(getState());
      await DELETE(`${getBasePath(isAdmin)}/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to delete subscription'));
    }
  },
);
 
export const sendEmailSubscription = createAsyncThunk(
  'manageEmails/sendSubscription',
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const isAdmin = getIsAdminApi(getState());
      const endpoint = `${getBasePath(isAdmin)}/${id}/send-and-download`;
      const url = getFullApiUrl(endpoint);
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          Accept: XLSX_MIME_TYPE,
        },
      });
      if (!response.ok) {
        const errorData = await response
          .clone()
          .json()
          .catch(() => null);
        throw {
          status: response.status,
          message: errorData?.message || `HTTP ${response.status}`,
          data: errorData,
        };
      }

      const data = await response.arrayBuffer();
      const contentType = response.headers.get('content-type') || XLSX_MIME_TYPE;
      const blob = toBlob(data) || new Blob([data], { type: contentType });
      if (!blob || blob.size === 0) {
        throw {
          status: 500,
          message: 'Received empty file from server',
          data: null,
        };
      }

      const fileName = getFileNameFromDisposition(
        response.headers.get('content-disposition'),
        `report-email-subscription-${id}.xlsx`,
      );

      triggerDownload(blob, fileName);

      return {
        id,
        result: {
          success: true,
          message: response.headers.get('x-email-message') || 'Report sent and downloaded.',
          emailSent: response.headers.get('x-email-sent') === 'true',
        },
      };
    } catch (err) {
      return rejectWithValue(normalizeThunkError(err, 'Unable to send email and download report'));
    }
  },
);
 
 
const manageEmailsSlice = createSlice({
  name: 'manageEmails',
  initialState,
  reducers: {},
  extraReducers: {
    [fetchEmailSubscriptions.pending]: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    [fetchEmailSubscriptions.rejected]: (state, action) => {
      state.isLoading = false;
      state.list = [];
      state.error = action.error?.message || 'Unable to load subscriptions';
    },
    [fetchEmailSubscriptions.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.list = action.payload;
      state.error = null;
    },
 
    [createEmailSubscription.pending]: (state) => {
      state.isSaving = true;
      state.error = null;
    },
    [createEmailSubscription.rejected]: (state, action) => {
      state.isSaving = false;
      state.error = action.payload?.message || action.error?.message || 'Unable to create subscription';
    },
    [createEmailSubscription.fulfilled]: (state, action) => {
      state.isSaving = false;
      state.list = [action.payload, ...(state.list || [])];
      state.error = null;
    },
 
    [deleteEmailSubscription.pending]: (state, action) => {
      const id = action.meta?.arg?.id;
      if (id) {
        state.loadingById[id] = true;
      }
    },
    [deleteEmailSubscription.rejected]: (state, action) => {
      const id = action.meta?.arg?.id;
      if (id) {
        state.loadingById[id] = false;
      }
      state.error = action.payload?.message || action.error?.message || 'Unable to delete subscription';
    },
    [deleteEmailSubscription.fulfilled]: (state, action) => {
      const id = action.payload;
      if (id) {
        state.loadingById[id] = false;
      }
      state.list = (state.list || []).filter((item) => item.id !== id);
      state.error = null;
    },
 
    [sendEmailSubscription.pending]: (state, action) => {
      const id = action.meta?.arg?.id;
      if (id) {
        state.loadingById[id] = true;
      }
    },
    [sendEmailSubscription.rejected]: (state, action) => {
      const id = action.meta?.arg?.id;
      if (id) {
        state.loadingById[id] = false;
      }
      state.error = action.payload?.message || action.error?.message || 'Unable to send email';
    },
    [sendEmailSubscription.fulfilled]: (state, action) => {
      const id = action.payload?.id;
      if (id) {
        state.loadingById[id] = false;
      }
      state.error = null;
    },
  },
});
 
const getManageEmailsStore = (state) => state.manageEmails;
 
export const getEmailSubscriptions = createSelector(getManageEmailsStore, (state) => state.list || []);
export const getEmailSubscriptionsLoading = createSelector(getManageEmailsStore, (state) => state.isLoading);
export const getEmailSubscriptionSaving = createSelector(getManageEmailsStore, (state) => state.isSaving);
export const getEmailSubscriptionLoadingById = createSelector(getManageEmailsStore, (state) => state.loadingById || {});
export const getEmailSubscriptionError = createSelector(getManageEmailsStore, (state) => state.error);
 
export default manageEmailsSlice.reducer;